import type { ProfileTaskVersionInput } from '../model/profile-task';

export type ProfileTaskValidationField =
    | 'effort.maximum'
    | 'effort.minimum'
    | 'effort.range'
    | 'requiredSkills'
    | 'title'
    | 'workflow.description'
    | 'workflow.identity'
    | 'workflow.title';

export type ProfileTaskValidationIssue = {
    readonly field: ProfileTaskValidationField;
    readonly translationKey: `pages.job-architecture-v2.profile-tasks.validation.${string}`;
    readonly workflowStepId?: string;
};

export type ProfileTaskValidationResult =
    | { readonly issues: readonly ProfileTaskValidationIssue[]; readonly ok: false }
    | { readonly ok: true; readonly value: ProfileTaskVersionInput };

const hasInvalidOpaqueId = (value: string): boolean => value.trim().length === 0;

const titleLineBreakPattern = /\s*[\r\n]+\s*/g;

export const normalizeProfileTaskTitle = (value: string): string => value.replace(titleLineBreakPattern, ' ');

const getEffortIssues = (input: ProfileTaskVersionInput): readonly ProfileTaskValidationIssue[] => {
    const issues: ProfileTaskValidationIssue[] = [];
    const minimum = input.effort?.minimumHoursPerWeek;
    const maximum = input.effort?.maximumHoursPerWeek;
    if (minimum !== undefined && (!Number.isFinite(minimum) || minimum < 0)) {
        issues.push({
            field: 'effort.minimum',
            translationKey: 'pages.job-architecture-v2.profile-tasks.validation.effortMinimum',
        });
    }
    if (maximum !== undefined && (!Number.isFinite(maximum) || maximum < 0)) {
        issues.push({
            field: 'effort.maximum',
            translationKey: 'pages.job-architecture-v2.profile-tasks.validation.effortMaximum',
        });
    }
    if (minimum !== undefined && maximum !== undefined && minimum > maximum) {
        issues.push({
            field: 'effort.range',
            translationKey: 'pages.job-architecture-v2.profile-tasks.validation.effortRange',
        });
    }
    return issues;
};

const getWorkflowIssues = (input: ProfileTaskVersionInput): readonly ProfileTaskValidationIssue[] => {
    const issues: ProfileTaskValidationIssue[] = [];
    const workflowIds = input.workflowSteps.map(({ workflowStepId }) => workflowStepId.trim());
    if (workflowIds.some(hasInvalidOpaqueId) || new Set(workflowIds).size !== workflowIds.length) {
        issues.push({
            field: 'workflow.identity',
            translationKey: 'pages.job-architecture-v2.profile-tasks.validation.workflowIdentity',
        });
    }
    for (const step of input.workflowSteps) {
        if (step.title.trim().length === 0) {
            issues.push({
                field: 'workflow.title',
                translationKey: 'pages.job-architecture-v2.profile-tasks.validation.workflowTitle',
                workflowStepId: step.workflowStepId,
            });
        }
        // The backend requires a non-blank description on every workflow step.
        if (step.description === undefined || step.description.trim().length === 0) {
            issues.push({
                field: 'workflow.description',
                translationKey: 'pages.job-architecture-v2.profile-tasks.validation.workflowDescription',
                workflowStepId: step.workflowStepId,
            });
        }
    }
    return issues;
};

export const validateProfileTask = (input: ProfileTaskVersionInput): ProfileTaskValidationResult => {
    const issues: ProfileTaskValidationIssue[] = [];
    const title = normalizeProfileTaskTitle(input.title).trim();

    if (title.length === 0) {
        issues.push({ field: 'title', translationKey: 'pages.job-architecture-v2.profile-tasks.validation.title' });
    }

    issues.push(...getEffortIssues(input));

    const skillIds = input.requiredSkillIds.map((skillId) => skillId.trim());
    if (skillIds.some(hasInvalidOpaqueId) || new Set(skillIds).size !== skillIds.length) {
        issues.push({
            field: 'requiredSkills',
            translationKey: 'pages.job-architecture-v2.profile-tasks.validation.requiredSkills',
        });
    }

    issues.push(...getWorkflowIssues(input));

    if (issues.length > 0) {
        return { issues, ok: false };
    }

    return {
        ok: true,
        value: {
            ...input,
            description: input.description?.trim(),
            classificationReasoning: input.classificationReasoning?.trim(),
            effortReasoning: input.effortReasoning?.trim(),
            requiredSkillIds: skillIds,
            title,
            workflowSteps: input.workflowSteps.map((step) => ({
                ...step,
                description: step.description?.trim(),
                title: step.title.trim(),
                workflowStepId: step.workflowStepId.trim(),
            })),
        },
    };
};
